import { useEffect, useState } from 'react';
import { findStoreCollection, type StoreCollection } from '../elements/render/useStoreCatalog';
import type { PageType } from '../themes/registry';
import type { CheckoutLanguage } from '../components/checkoutLabels';
import { countWords, shopWords, type ShopWords } from '../commerce/shopWords';

// The collection a collection page is about, as the facts its bound title, description and count read. The
// address names it — /collections/face-care — and the store's collection list, which already carries each
// one's description and product count, answers it. Where the address names none, as in the builder or a
// theme preview, the page is about all products, which is what its product grid then shows.

/** What a collection page knows about its collection. */
export type CollectionFacts = { title: string; image: string; href: string; count: string; description: string };

// The collection every store has, all of its products, named in the store's language.
const allOf = (words: ShopWords): CollectionFacts => ({ title: words.allProducts, image: '', href: '/collections/all', count: '', description: '' });

// What the page says when the address names a collection the shop does not have; its grid offers the way on.
const missingOf = (words: ShopWords): CollectionFacts => ({ title: words.collectionNotFound, image: '', href: '/collections/all', count: '', description: '' });

// Reads the collection handle the address names, or null on any other address.
const handleInAddress = (): string | null => window.location.pathname.match(/^\/collections\/([^/]+)/)?.[1] ?? null;

// Reads one collection as the page's facts, its count in the store's language.
const factsOf = (row: StoreCollection, words: ShopWords): CollectionFacts => ({
  title: row.title, image: row.imageUrl ?? '', href: `/collections/${row.slug}`, description: row.description ?? '', count: countWords(row.productCount, words)
});

// Reads the facts of the collection a collection page is about; nothing on any other page.
export function usePageCollection(page: PageType, language: CheckoutLanguage | null): CollectionFacts | null {
  const [facts, setFacts] = useState<CollectionFacts | null>(null);
  useEffect(() => {
    if (page !== 'collection') return setFacts(null);
    const handle = handleInAddress();
    const words = shopWords(language);
    if (handle === null || handle === 'all') return setFacts(allOf(words));
    let live = true;
    findStoreCollection(handle)
      .then((row) => { if (live) setFacts(row ? factsOf(row, words) : missingOf(words)); })
      .catch(() => { if (live) setFacts(null); });
    return () => { live = false; };
  }, [page, language]);
  return facts;
}
